import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { AssistantContextSummary } from '../types';

export type AssistantContextStorage<TContext> = {
  read?: () => Partial<TContext>;
  write?: (next: Partial<TContext>) => void;
};

export type AssistantContextFetchResult<TContext> = {
  summary?: AssistantContextSummary;
  nextContext?: Partial<TContext>;
};

export type UseAssistantContextOptions<TContext> = {
  initialContext?: TContext;
  isActive?: boolean;
  storage?: AssistantContextStorage<TContext>;
  fetchContext?: (context: TContext) => Promise<AssistantContextFetchResult<TContext>>;
  onError?: (error: unknown) => string;
  delayMs?: number;
};

export type UseAssistantContextResult<TContext> = {
  context: TContext;
  setContext: Dispatch<SetStateAction<TContext>>;
  summary: AssistantContextSummary;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  readStoredContext: () => Partial<TContext>;
};

const mergeIfChanged = <TContext extends Record<string, unknown>>(
  previous: TContext,
  updates: Partial<TContext>,
): TContext => {
  const next = { ...previous, ...updates } as TContext;
  const changed = Object.keys(updates).some((key) => {
    const typedKey = key as keyof TContext;
    return previous[typedKey] !== next[typedKey];
  });
  return changed ? next : previous;
};

export function useAssistantContext<TContext>({
  initialContext,
  isActive = true,
  storage,
  fetchContext,
  onError,
  delayMs = 200,
}: UseAssistantContextOptions<TContext>): UseAssistantContextResult<TContext> {
  const [context, setContext] = useState<TContext>(initialContext ?? ({} as TContext));
  const [summary, setSummary] = useState<AssistantContextSummary>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const didLoadStoredContext = useRef(false);

  const readStoredContext = useCallback((): Partial<TContext> => {
    if (!storage?.read) {
      return {};
    }
    try {
      return storage.read() ?? {};
    } catch (readError) {
      console.warn('[AI Assistant] Failed to read stored context', readError);
      return {};
    }
  }, [storage]);

  useEffect(() => {
    if (didLoadStoredContext.current) {
      return;
    }
    didLoadStoredContext.current = true;
    const storedContext = readStoredContext();
    if (Object.keys(storedContext).length > 0) {
      setContext((prev) => mergeIfChanged(
        prev as Record<string, unknown>,
        storedContext as Partial<Record<string, unknown>>,
      ) as TContext);
    }
  }, [readStoredContext]);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!fetchContext) {
      setIsLoading(false);
      setError(null);
      setSummary({});
      return;
    }
    if (!isActive) {
      setIsLoading(false);
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await fetchContext(context);
        if (!active) {
          return;
        }
        setSummary(result.summary ?? {});
        if (result.nextContext && Object.keys(result.nextContext).length > 0) {
          let didChange = false;
          setContext((prev) => {
            const merged = mergeIfChanged(
              prev as Record<string, unknown>,
              result.nextContext as Partial<Record<string, unknown>>,
            ) as TContext;
            didChange = merged !== prev;
            return merged;
          });
          if (didChange) {
            storage?.write?.(result.nextContext);
          }
        }
      } catch (fetchError) {
        if (!active) {
          return;
        }
        const message = onError
          ? onError(fetchError)
          : (fetchError instanceof Error ? fetchError.message : 'Unable to load context details.');
        setError(message);
        setSummary({});
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }, delayMs);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [context, fetchContext, isActive, onError, delayMs, storage, refreshKey]);

  return {
    context,
    setContext,
    summary,
    isLoading,
    error,
    refresh,
    readStoredContext,
  };
}
